import React, { Component } from "react";
import { connect } from "react-redux";
import moment from "moment";
import _ from "lodash";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { P60Icon } from "../../components";
import { getPluralS } from "../../helper";
import { COLOUR_RED } from "../../js";

export class BookingCalendar extends Component {
	state = {
		shownMonth: new Date(),
		grid: [],
	};

	UNSAFE_componentWillMount() {
		this.onNewProps(this.props, true);
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		this.onNewProps(nextProps);
	}

	onNewProps(nextProps, forceUpdate) {
		// Only regenerate grid when props actually change that require it
		if (
			forceUpdate ||
			nextProps.selectedDate !== this.props.selectedDate ||
			nextProps.bookingList.length !== this.props.bookingList.length ||
			nextProps.view !== this.props.view
		) {
			let dateToUse = nextProps.selectedDate;
			let selectedDate = dateToUse;
			let shouldUpdateState = false;

			// Only update dates if we have a specific selectedDate or this is the initial load
			if (!(dateToUse instanceof Date)) {
				if (!dateToUse) {
					// On initial load or when no selectedDate, use current dates but don't reset navigation
					if (forceUpdate) {
						// Initial load - set to current date
						dateToUse = new Date();
						shouldUpdateState = true;
					} else {
						// Props update - keep existing navigation state, just regenerate grid with new bookings
						dateToUse =
							nextProps.view === "week"
								? this.state.shownWeek
								: this.state.shownMonth;
						shouldUpdateState = false;
					}
					selectedDate = null;
				} else {
					dateToUse = new Date(moment(dateToUse.dateKey, "DD-MM-YYYY"));
					selectedDate = dateToUse;
					shouldUpdateState = true;
				}
			} else {
				shouldUpdateState = true;
			}

			// Only update state if needed
			if (shouldUpdateState) {
				this.setState(
					{
						shownMonth: dateToUse,
						shownWeek: moment(dateToUse).endOf("w").toDate(),
						selectedDate: selectedDate,
					},
					() => {
						this.setMonth();
						this.setWeek();
						// Generate grid after state update
						this.generateGrid(
							dateToUse,
							selectedDate,
							nextProps.bookingList,
							nextProps.view,
						);
					},
				);
			} else {
				// Just regenerate grid with current state and new booking data
				this.generateGrid(
					dateToUse,
					selectedDate,
					nextProps.bookingList,
					nextProps.view,
				);
			}
		}
	}

	setMonth = () => {
		// Disabled to prevent infinite API loops when parent component updates
		// The parent component (Bookings.js) handles month changes directly
	};

	setWeek = () => {
		// Disabled to prevent infinite API loops when parent component updates
		// The parent component (Bookings.js) handles week changes directly
	};

	getShortDate() {
		if (!this.props.selectedDate) {
			return "";
		}
		return moment(this.props.selectedDate).format("DD/MM/YYYY");
	}

	getText() {
		if (this.props.view === "week") {
			return `Week ${moment(this.state.shownWeek).format("W")} ${moment(this.state.shownWeek).startOf("w").format("YYYY")}`;
		}
		return moment(this.state.shownMonth).format("MMMM YYYY");
	}

	change = (change) => {
		if (this.props.view === "week") {
			this.changeWeek(change);
		} else {
			this.changeMonth(change);
		}
	};

	changeWeek(change) {
		const newShownDate = moment(this.state.shownWeek)
			.add(change, "w")
			.endOf("w")
			.toDate();
		this.setState(
			{
				shownWeek: newShownDate,
				shownMonth: newShownDate, // Update month state too for consistency
			},
			() => {
				// Generate grid after state is updated
				this.generateGrid(newShownDate, null, this.props.bookingList);
			},
		);

		// Notify parent component of week change
		if (this.props.changeWeek) {
			const weekStart = moment(newShownDate).startOf("w").toDate();
			this.props.changeWeek(weekStart);
		}

		if (this.state.selectedDate) {
			setTimeout(() => {
				this.props.selectDate(null);
			}, 0);
		}
	}

	changeMonth(change) {
		const newShownDate = new Date(
			this.state.shownMonth.getFullYear(),
			this.state.shownMonth.getMonth() + 1 + change,
			0,
		);
		this.setState({
			shownMonth: newShownDate,
			shownWeek: moment(newShownDate).endOf("w").toDate(), // Update week state too for consistency
		});
		this.generateGrid(newShownDate, null, this.props.bookingList);

		// Notify parent component so it can fetch bookings for the selected month.
		if (this.props.changeMonth) {
			this.props.changeMonth(
				newShownDate.getFullYear(),
				newShownDate.getMonth() + 1,
			);
		}

		if (this.state.selectedDate) {
			setTimeout(() => {
				this.props.selectDate(null);
			}, 0);
		}
	}

	generateGrid(date, selectedDate, bookings, view) {
		const month = moment(date);
		const viewToUse = view || this.props.view;
		let dateToTarget =
			viewToUse === "week"
				? moment(date).endOf("week")
				: moment(date).endOf("month");
		let dateToModify =
			viewToUse === "week"
				? moment(date).startOf("week")
				: moment(date).startOf("month");
		const selectedDateToUse = selectedDate ? moment(selectedDate) : null;

		// set dateToModify to be Sunday of that week
		while (dateToModify.day() !== 0) {
			dateToModify = dateToModify.add(-1, "day");
		}

		const rows = [[]];

		while (dateToTarget.day() !== 6) {
			dateToTarget = dateToTarget.add(1, "day");
		}
		dateToTarget = dateToTarget.add(1, "day"); // add an extra day to complete the week

		while (!dateToModify.isSame(dateToTarget, "day")) {
			if (rows[rows.length - 1].length === 7) {
				// week has filled - add new row
				rows.push([]);
			}
			const thisDate = moment(dateToModify);
			const dateKey = thisDate.format("DD-MM-YYYY");

			const dayBookings = _.filter(bookings, (b) => {
				return b.DateKey === dateKey;
			});

			// In week view, don't fade any dates; in month view, fade dates outside current month
			const isFaded =
				viewToUse === "week" ? false : !thisDate.isSame(month, "month");

			rows[rows.length - 1].push({
				date: thisDate,
				dateKey: dateKey,
				display: thisDate.date(),
				isFaded: isFaded,
				isToday: thisDate.isSame(moment(), "date"),
				isSelected:
					selectedDateToUse && thisDate.isSame(selectedDateToUse, "date"),
				bookings: dayBookings,
			});

			dateToModify = dateToModify.add(1, "day");
		}

		this.setState({
			grid: rows,
		});
	}

	renderGrid() {
		return this.state.grid.map((row, index) => {
			return (
				<div className="eventCalendar__row" key={index}>
					{row.map((date, dateIndex) => {
						let classes = "eventCalendar__date";
						if (date.isFaded) {
							classes += " eventCalendar__date--faded";
						}
						if (date.isToday) {
							classes += " eventCalendar__date--today";
						}
						if (date.isSelected) {
							classes += " eventCalendar__date--selected";
						}
						if (index + 1 === this.state.grid.length) {
							classes += " noBottomBorder";
						}
						if (dateIndex === 0) {
							classes += " noLeftBorder";
						}
						if (dateIndex === 6) {
							classes += " noRightBorder";
						}
						return (
							<div className={classes} key={date.dateKey}>
								<div className="eventCalendar__date__content">
									<div className="eventCalendar__date__inner">
										{date.display}
									</div>
									{date.bookings.map((booking, i) => {
										if (i > 2 && this.props.view !== "week") {
											return null;
										}
										return (
											<Link
												className="noUnderline"
												to={`/bookings/booking/${booking.Id}`}
												key={i}
											>
												<div className="eventCalendar__event">
													<span className="eventCalendar__eventTime">
														{booking.Time}
														{booking.EndTime ? ` - ${booking.EndTime}` : ""}
													</span>
													<span
														className="eventCalendar__eventTitle"
														alt={booking.Title}
														style={
															booking.IsBlockOut
																? { color: COLOUR_RED }
																: undefined
														}
													>
														{booking.Id}
													</span>
													<span className="eventCalendar__eventLocation">
														<br />
														{booking.Location}
													</span>
													{booking.User && booking.User.displayName && (
														<span className="eventCalendar__eventLocation">
															<br />
															{booking.User.displayName}
														</span>
													)}
													{booking.BookedBy &&
														booking.BookedBy.displayName &&
														booking.User &&
														booking.BookedBy.displayName !==
															booking.User.displayName && (
															<span className="eventCalendar__eventBookedBy">
																<br />
																Booked by: {booking.BookedBy.displayName}
															</span>
														)}
													{booking.Cancelled && (
														<span
															style={{ color: COLOUR_RED, fontSize: "11px" }}
														>
															<br />
															(Cancelled)
														</span>
													)}
												</div>
											</Link>
										);
									})}
									{date.bookings.length > 3 && this.props.view !== "week" && (
										<div
											className="eventCalendar__moreEvents"
											onClick={() => {
												this.props.selectDate(date);
											}}
										>
											{`+${date.bookings.length - 3} more booking${getPluralS(date.bookings.length - 3)}`}
										</div>
									)}
								</div>
							</div>
						);
					})}
				</div>
			);
		});
	}

	render() {
		return (
			<div
				className={`eventCalendar ${this.state.selectedDate != null ? "eventCalendar--hasSelected" : ""}${
					this.props.view === "week" ? " eventCalendar--weekView" : ""
				}`}
			>
				<div className="eventCalendar__bottom">
					<div className="eventCalendar__monthSelector">
						<P60Icon
							className="eventCalendar__monthChevron"
							icon="chevron_left"
							onClick={() => {
								this.change(-1);
							}}
						/>
						<p className="eventCalendar__selectedMonth">{this.getText()}</p>
						<P60Icon
							className="eventCalendar__monthChevron"
							icon="chevron_right"
							onClick={() => {
								this.change(1);
							}}
						/>
					</div>
					<div className="eventCalendar__row marginTop-16">
						<p className="eventCalendar__colHeader noTopBorder noLeftBorder">
							Sun
						</p>
						<p className="eventCalendar__colHeader noTopBorder">Mon</p>
						<p className="eventCalendar__colHeader noTopBorder">Tue</p>
						<p className="eventCalendar__colHeader noTopBorder">Wed</p>
						<p className="eventCalendar__colHeader noTopBorder">Thu</p>
						<p className="eventCalendar__colHeader noTopBorder">Fri</p>
						<p className="eventCalendar__colHeader noTopBorder noRightBorder">
							Sat
						</p>
					</div>
					{this.renderGrid()}
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state) => ({ state });

export default connect(mapStateToProps, {})(withRouter(BookingCalendar));
